import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import client from '../../API';
import UploadFileDialog from '../../Components/tools/uploadfile';
import CreateAssetDialog from '../../Components/tools/createasset';
import CreateContractDialog from '../../Components/tools/createcontract';

import {
  Button,
  FormControlLabel,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Menu,
  MenuItem,
  Autocomplete,
  Snackbar,
  Checkbox,
  Tooltip,
  LinearProgress,
  FormControl,
  Select,
  Tabs,
  Tab,
  ListItemIcon,
  Avatar,
  InputLabel,
  useMediaQuery,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  Collapse,
  InputAdornment,
} from '@mui/material';

import {
  PictureAsPdf,
  Description,
  Image,
  UploadFile,
  Folder,
  InsertDriveFile,
  Assignment,
  Search,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  DriveFileMoveOutlined as MoveIcon,
  CheckBox as CheckSquareIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  CloudUpload as CloudUploadIcon,
  AddTask as AddTaskIcon,
  Share as ShareIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Visibility,
  Launch as LaunchIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';

// Constants matching AppShell
const APPSHELL_BAR_HEIGHT = 86;
const SPEED_DIAL_MARGIN = 16;
const CONTENT_BOTTOM_PADDING = APPSHELL_BAR_HEIGHT + SPEED_DIAL_MARGIN;

const AssetOrganizerSeg = ({ onRefresh, files }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // View state
  const [viewMode, setViewMode] = useState(isMobile ? 'grid' : 'grid');
  const [viewFilter, setViewFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createAssetDialogOpen, setCreateAssetDialogOpen] = useState(false);
  const [createContractDialogOpen, setCreateContractDialogOpen] = useState(false);
  const [createOrganizerDialogOpen, setCreateOrganizerDialogOpen] = useState(false);
  const [editOrganizerDialogOpen, setEditOrganizerDialogOpen] = useState(false);
  const [addFilesToOrganizerDialogOpen, setAddFilesToOrganizerDialogOpen] = useState(false);
  
  // Sharing dialog states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [viewSharesDialogOpen, setViewSharesDialogOpen] = useState(false);
  
  // Enhanced checklist states
  const [createChecklistDialogOpen, setCreateChecklistDialogOpen] = useState(false);
  const [uploadWithChecklistDialogOpen, setUploadWithChecklistDialogOpen] = useState(false);
  
  // Form states
  const [newOrganizerForm, setNewOrganizerForm] = useState({
    name: '',
    description: '',
    organizer_type: 'general'
  });
  const [editingOrganizer, setEditingOrganizer] = useState(null);
  const [selectedOrganizerForAdd, setSelectedOrganizerForAdd] = useState(null);
  const [selectedOrganizerForChecklist, setSelectedOrganizerForChecklist] = useState(null);
  
  // Sharing form states
  const [selectedOrganizerForShare, setSelectedOrganizerForShare] = useState(null);
  const [shareForm, setShareForm] = useState({
    emails: [],
    permission_level: 'read',
    message: ''
  });
  const [shareEmailInput, setShareEmailInput] = useState('');
  
  // Enhanced checklist form states
  const [newChecklistItem, setNewChecklistItem] = useState({
    title: '',
    description: '',
    required: true,
    expected_file_types: [],
    notes: ''
  });
  const [bulkChecklistText, setBulkChecklistText] = useState('');
  const [checklistView, setChecklistView] = useState('single');
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);
  
  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // API Queries
  const getOrganizersParams = () => ({
    include_files: true,
    include_checklist: true,
    include_sharing: true,
    view: viewFilter
  });

  const {
    data: organizers,
    isLoading: organizersLoading,
    error: organizersError,
    refetch: refetchOrganizers
  } = useQuery(['organizers', viewFilter], () => client.files.organizers.list(getOrganizersParams()), {
    initialData: [],
    initialDataUpdatedAt: 0
  });

  const {
    data: organizerTypes,
    isLoading: typesLoading
  } = useQuery('organizer-types', () => client.files.organizers.getTypes(), {
    initialData: [],
    initialDataUpdatedAt: 0
  });

  // Mutations
  const createOrganizerMutation = useMutation(
    (data) => client.files.organizers.create(data),
    {
      onSuccess: () => {
        showSnackbar('Folder created successfully');
        refetchOrganizers();
        onRefresh?.();
        setCreateOrganizerDialogOpen(false);
        setNewOrganizerForm({ name: '', description: '', organizer_type: 'general' });
      },
      onError: (error) => {
        showSnackbar('Error creating folder', 'error');
        console.error('Create organizer error:', error);
      }
    }
  );

  const updateOrganizerMutation = useMutation(
    ({ id, data }) => client.files.organizers.update(id, data),
    {
      onSuccess: () => {
        showSnackbar('Folder updated successfully');
        refetchOrganizers();
        onRefresh?.();
        setEditOrganizerDialogOpen(false);
        setEditingOrganizer(null);
      },
      onError: (error) => {
        showSnackbar('Error updating folder', 'error');
        console.error('Update organizer error:', error);
      }
    }
  );

  const deleteOrganizerMutation = useMutation(
    (id) => client.files.organizers.delete(id),
    {
      onSuccess: () => {
        showSnackbar('Folder deleted successfully');
        refetchOrganizers();
        onRefresh?.();
      },
      onError: (error) => {
        showSnackbar('Error deleting folder', 'error');
        console.error('Delete organizer error:', error);
      }
    }
  );

  const addFilesToOrganizerMutation = useMutation(
    ({ organizerId, fileIds }) => client.files.organizers.addExistingFiles(organizerId, { file_ids: fileIds }),
    {
      onSuccess: (data) => {
        const addedCount = data.added_files?.length || fileIds.length;
        showSnackbar(`Added ${addedCount} files to folder`);
        refetchOrganizers();
        onRefresh?.();
        setAddFilesToOrganizerDialogOpen(false);
        setSelectedOrganizerForAdd(null);
        setSelectedItems([]);
        setSelectionMode(false);
      },
      onError: (error) => {
        showSnackbar('Error adding files to folder', 'error');
        console.error('Add files error:', error);
      }
    }
  );

  // Checklist mutations
  const createChecklistItemMutation = useMutation(
    ({ organizerId, data }) => client.files.organizers.createChecklistItem(organizerId, data),
    {
      onSuccess: () => {
        showSnackbar('Checklist item created successfully');
        refetchOrganizers();
        onRefresh?.();
        setNewChecklistItem({
          title: '',
          description: '',
          required: true,
          expected_file_types: [],
          notes: ''
        });
      },
      onError: (error) => {
        showSnackbar('Error creating checklist item', 'error');
        console.error('Create checklist item error:', error);
      }
    }
  );

  const createBulkChecklistItemsMutation = useMutation(
    ({ organizerId, items }) => client.files.organizers.createBulkChecklistItems(organizerId, { items }),
    {
      onSuccess: (data) => {
        showSnackbar(`Created ${data.items?.length || 0} checklist items successfully`);
        refetchOrganizers();
        onRefresh?.();
        setBulkChecklistText('');
        setCreateChecklistDialogOpen(false);
      },
      onError: (error) => {
        showSnackbar('Error creating checklist items', 'error');
        console.error('Bulk create checklist items error:', error);
      }
    }
  );

  // Sharing mutations
  const shareOrganizerMutation = useMutation(
    ({ organizerId, data }) => client.files.organizers.shareOrganizer(organizerId, data),
    {
      onSuccess: (data) => {
        const successCount = data.total_shared || 0;
        const errorCount = data.total_errors || 0;
        
        if (successCount > 0) {
          showSnackbar(`Successfully shared with ${successCount} user${successCount > 1 ? 's' : ''}`, 'success');
        }
        if (errorCount > 0) {
          showSnackbar(`${errorCount} error${errorCount > 1 ? 's' : ''} occurred during sharing`, 'warning');
        }
        
        refetchOrganizers();
        onRefresh?.();
        setShareDialogOpen(false);
        setShareForm({ emails: [], permission_level: 'read', message: '' });
        setShareEmailInput('');
      },
      onError: (error) => {
        showSnackbar('Error sharing organizer', 'error');
        console.error('Share organizer error:', error);
      }
    }
  );

  const removeShareMutation = useMutation(
    ({ organizerId, shareId }) => client.files.organizers.removeOrganizerShare(organizerId, shareId),
    {
      onSuccess: () => {
        showSnackbar('Share removed successfully');
        refetchOrganizers();
        onRefresh?.();
      },
      onError: (error) => {
        showSnackbar('Error removing share', 'error');
        console.error('Remove share error:', error);
      }
    }
  );

  // Helper Functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isImageFile = (filename) => {
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
    return imageExtensions.some((ext) => filename?.toLowerCase().endsWith(ext));
  };

  // Selection functions
  const toggleItemSelection = (itemId, itemType) => {
    if (itemType === 'file') {
      setSelectedItems(prev => {
        const itemKey = `${itemType}_${itemId}`;
        return prev.includes(itemKey) 
          ? prev.filter(id => id !== itemKey)
          : [...prev, itemKey];
      });
    }
  };

  const isItemSelected = (itemId, itemType) => {
    return selectedItems.includes(`${itemType}_${itemId}`);
  };

  const getSelectedFiles = () => {
    return selectedItems
      .filter(item => item.startsWith('file_'))
      .map(item => parseInt(item.split('_')[1]));
  };

  // Event handlers
  const handleItemClick = (item, itemType) => {
    if (selectionMode && itemType === 'file') {
      toggleItemSelection(item.id, itemType);
    } else if (selectionMode && itemType === 'folder') {
      return;
    } else {
      if (itemType === 'folder') {
        navigate(`/organizers/${item.id}`);
      } else {
        if (item.type === 'contract') {
          navigate(`/contract/${item.id}`);
        } else {
          navigate(`/files/${item.id}`);
        }
      }
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries('files');
    queryClient.invalidateQueries('contracts');
    queryClient.invalidateQueries('organizers');
    onRefresh?.();
  };

  // Dialog Handlers
  const openUploadDialog = () => setUploadDialogOpen(true);
  const closeUploadDialog = () => {
    setUploadDialogOpen(false);
    handleSuccess();
  };

  const openCreateAssetDialog = () => setCreateAssetDialogOpen(true);
  const closeCreateAssetDialog = () => {
    setCreateAssetDialogOpen(false);
    handleSuccess();
  };

  const openCreateContractDialog = () => setCreateContractDialogOpen(true);
  const closeCreateContractDialog = () => {
    setCreateContractDialogOpen(false);
    handleSuccess();
  };

  // Enhanced checklist handlers
  const handleCreateChecklist = (organizer) => {
    setSelectedOrganizerForChecklist(organizer);
    setCreateChecklistDialogOpen(true);
    handleMenuClose();
  };

  const handleUploadWithChecklist = (organizer) => {
    setSelectedOrganizerForChecklist(organizer);
    setUploadWithChecklistDialogOpen(true);
    handleMenuClose();
  };

  // Sharing handlers
  const handleShareOrganizer = (organizer) => {
    setSelectedOrganizerForShare(organizer);
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleViewShares = (organizer) => {
    setSelectedOrganizerForShare(organizer);
    setViewSharesDialogOpen(true);
    handleMenuClose();
  };

  const handleAddEmail = () => {
    const email = shareEmailInput.trim().toLowerCase();
    if (email && !shareForm.emails.includes(email)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        setShareForm(prev => ({
          ...prev,
          emails: [...prev.emails, email]
        }));
        setShareEmailInput('');
      } else {
        showSnackbar('Please enter a valid email address', 'error');
      }
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setShareForm(prev => ({
      ...prev,
      emails: prev.emails.filter(email => email !== emailToRemove)
    }));
  };

  const handleShareSubmit = () => {
    if (!selectedOrganizerForShare || shareForm.emails.length === 0) {
      showSnackbar('Please add at least one email address', 'error');
      return;
    }

    shareOrganizerMutation.mutate({
      organizerId: selectedOrganizerForShare.id,
      data: shareForm
    });
  };

  const handleRemoveShare = (shareId) => {
    if (selectedOrganizerForShare && window.confirm('Are you sure you want to remove this share?')) {
      removeShareMutation.mutate({
        organizerId: selectedOrganizerForShare.id,
        shareId: shareId
      });
    }
  };

  // Organizer handlers
  const handleCreateOrganizer = () => {
    createOrganizerMutation.mutate(newOrganizerForm);
  };

  const handleEditOrganizer = (organizer) => {
    setEditingOrganizer({
      ...organizer,
      organizer_type: organizer.organizer_type || 'general'
    });
    setEditOrganizerDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdateOrganizer = () => {
    updateOrganizerMutation.mutate({
      id: editingOrganizer.id,
      data: {
        name: editingOrganizer.name,
        description: editingOrganizer.description,
        organizer_type: editingOrganizer.organizer_type,
        metadata: editingOrganizer.metadata
      }
    });
  };

  const handleDeleteOrganizer = (organizerId) => {
    if (window.confirm('Are you sure you want to delete this folder?')) {
      deleteOrganizerMutation.mutate(organizerId);
    }
    handleMenuClose();
  };

  const handleAddFilesToOrganizer = () => {
    if (selectedOrganizerForAdd && getSelectedFiles().length > 0) {
      addFilesToOrganizerMutation.mutate({
        organizerId: selectedOrganizerForAdd.id,
        fileIds: getSelectedFiles()
      });
    }
  };

  const handleCreateChecklistItem = () => {
    if (!selectedOrganizerForChecklist || !newChecklistItem.title) return;
    
    createChecklistItemMutation.mutate({
      organizerId: selectedOrganizerForChecklist.id,
      data: newChecklistItem
    });
  };

  const handleBulkCreate = () => {
    if (!bulkChecklistText.trim() || !selectedOrganizerForChecklist) return;
    
    const lines = bulkChecklistText.split('\n').filter(line => line.trim());
    const items = lines.map(line => ({
      title: line.trim(),
      required: true,
      expected_file_types: []
    }));
    
    createBulkChecklistItemsMutation.mutate({
      organizerId: selectedOrganizerForChecklist.id,
      items
    });
  };

  const handleMenuOpen = (event, target) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuTarget(target);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTarget(null);
  };

  // Helper function to render sharing indicators
  const renderSharingIndicator = (organizer) => {
    if (!organizer.is_shared && organizer.access_type === 'owner') {
      return null;
    }

    if (organizer.access_type === 'shared') {
      const sharedInfo = organizer.shared_info;
      return (
        <Tooltip title={`Shared by ${sharedInfo?.shared_by} • ${sharedInfo?.permission_level} access`}>
          <Chip
            size="small"
            icon={<ShareIcon sx={{ fontSize: 12 }} />}
            label={isMobile ? sharedInfo?.permission_level?.charAt(0).toUpperCase() : `Shared (${sharedInfo?.permission_level})`}
            color="secondary"
            variant="outlined"
            sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', height: 18 }}
          />
        </Tooltip>
      );
    }

    if (organizer.is_shared && organizer.access_type === 'owner') {
      const shareCount = organizer.shares?.length || 0;
      return (
        <Tooltip title={`Shared with ${shareCount} user${shareCount !== 1 ? 's' : ''}`}>
          <Chip
            size="small"
            icon={<PeopleIcon sx={{ fontSize: 12 }} />}
            label={shareCount}
            color="info"
            variant="outlined"
            sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem', height: 18 }}
          />
        </Tooltip>
      );
    }

    return null;
  };

  // Get unorganized files for display
  const getUnorganizedFiles = () => {
    const organizedFileIds = new Set();
    (organizers || []).forEach(org => {
      if (org.files) {
        org.files.forEach(file => organizedFileIds.add(file.id));
      }
    });
    return files.filter(file => !organizedFileIds.has(file.id));
  };

  const unorganizedFiles = getUnorganizedFiles();

  // Filter based on search
  const filteredFolders = searchQuery 
    ? (organizers || []).filter(folder => 
        folder.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : (organizers || []);

  const filteredFiles = searchQuery
    ? unorganizedFiles.filter(file =>
        (file.name || file.filename).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : unorganizedFiles;

  // Render functions
  const renderFileIcon = (file, size = 'large') => {
    const iconProps = { fontSize: size };
    if (file.type === 'contract') return <Assignment {...iconProps} color="primary" />;
    if (file.fileType === 'image') return <Image {...iconProps} />;
    if (file.fileType === 'pdf') return <PictureAsPdf {...iconProps} />;
    return <Description {...iconProps} />;
  };

  // Speed Dial Actions for Mobile
  const speedDialActions = [
    {
      icon: <UploadFile />,
      name: 'Upload Files',
      action: openUploadDialog,
    },
    {
      icon: <CreateNewFolderIcon />,
      name: 'New Folder',
      action: () => setCreateOrganizerDialogOpen(true),
    },
    ...(selectionMode && getSelectedFiles().length > 0 ? [{
      icon: <MoveIcon />,
      name: 'Move to Folder',
      action: () => setAddFilesToOrganizerDialogOpen(true),
    }] : []),
  ];

  if (organizersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (organizersError) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        {organizersError.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      pb: isMobile ? `calc(${CONTENT_BOTTOM_PADDING}px + env(safe-area-inset-bottom, 0px))` : 2,
      bgcolor: 'background.default'
    }}>
      <Container maxWidth="xl" disableGutters={isMobile}>
        {/* Mobile-Optimized Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: isMobile ? 1.5 : 3,
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'background.paper'
          }}
        >
          {/* Title Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isMobile ? 1.5 : 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Folder color="primary" sx={{ fontSize: isMobile ? 28 : 32 }} />
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
                Asset Manager
              </Typography>
            </Box>
            
            {!isMobile && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<CreateNewFolderIcon />}
                  onClick={() => setCreateOrganizerDialogOpen(true)}
                  size="small"
                >
                  New Folder
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadFile />}
                  onClick={openUploadDialog}
                  size="small"
                >
                  Upload
                </Button>
              </Stack>
            )}
          </Box>

          {/* Filter and View Controls */}
          <Stack spacing={1.5}>
            {/* Search Bar with Collapsible on Mobile */}
            {isMobile ? (
              <Box>
                {!showSearch ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={viewFilter}
                        onChange={(e) => setViewFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Folders</MenuItem>
                        <MenuItem value="owned">My Folders</MenuItem>
                        <MenuItem value="shared">Shared with Me</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => setShowSearch(true)}
                      color="primary"
                      sx={{ border: 1, borderColor: 'divider' }}
                    >
                      <Search />
                    </IconButton>
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder="Search files and folders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowSearch(false);
                              setSearchQuery('');
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Box>
            ) : (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search files and folders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flexGrow: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={viewFilter}
                    onChange={(e) => setViewFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Folders</MenuItem>
                    <MenuItem value="owned">My Folders</MenuItem>
                    <MenuItem value="shared">Shared with Me</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* Selection Mode and View Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectionMode}
                    onChange={(e) => {
                      setSelectionMode(e.target.checked);
                      setSelectedItems([]);
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {selectionMode ? `${getSelectedFiles().length} selected` : 'Select Files'}
                  </Typography>
                }
              />
              
              {!isMobile && (
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => setViewMode('grid')}
                    color={viewMode === 'grid' ? 'primary' : 'default'}
                  >
                    <GridViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setViewMode('list')}
                    color={viewMode === 'list' ? 'primary' : 'default'}
                  >
                    <ListViewIcon />
                  </IconButton>
                </Stack>
              )}
            </Box>

            {/* Selection Actions Bar */}
            <Collapse in={selectionMode && getSelectedFiles().length > 0}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  bgcolor: 'primary.lighter',
                  border: 1,
                  borderColor: 'primary.main',
                  borderRadius: 2
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip 
                    label={`${getSelectedFiles().length} file${getSelectedFiles().length !== 1 ? 's' : ''}`}
                    color="primary"
                    size="small"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<MoveIcon />}
                    onClick={() => setAddFilesToOrganizerDialogOpen(true)}
                    fullWidth={isMobile}
                  >
                    Move to Folder
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedItems([]);
                      setSelectionMode(false);
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Stack>
              </Paper>
            </Collapse>
          </Stack>
        </Paper>

        {/* Content Area */}
        <Box sx={{ px: isMobile ? 1 : 2 }}>
          {viewMode === 'grid' ? (
            <Box>
              {/* Folders Section */}
              {filteredFolders.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 2,
                    px: isMobile ? 0.5 : 0 
                  }}>
                    <Folder color="primary" fontSize="small" />
                    <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600 }}>
                      Folders ({filteredFolders.length})
                    </Typography>
                    {viewFilter === 'shared' && (
                      <Chip label="Shared" size="small" color="secondary" />
                    )}
                    {viewFilter === 'owned' && (
                      <Chip label="My folders" size="small" color="primary" />
                    )}
                  </Box>
                  
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                    {filteredFolders.map(folder => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={folder.id}>
                        <Card
                          onClick={() => handleItemClick(folder, 'folder')}
                          sx={{
                            cursor: selectionMode ? 'default' : 'pointer',
                            height: '100%',
                            transition: 'all 0.2s',
                            opacity: selectionMode ? 0.7 : 1,
                            '&:hover': selectionMode ? {} : {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            },
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <CardContent sx={{ 
                            position: 'relative', 
                            textAlign: 'center',
                            p: isMobile ? 1.5 : 2,
                            '&:last-child': { pb: isMobile ? 1.5 : 2 }
                          }}>
                            {/* Folder Icon with Badge */}
                            <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                              <Folder 
                                sx={{ 
                                  fontSize: isMobile ? 48 : 56, 
                                  color: folder.access_type === 'shared' ? 'secondary.main' : 'primary.main'
                                }} 
                              />
                              {folder.access_type === 'shared' && (
                                <Avatar
                                  sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 18,
                                    height: 18,
                                    bgcolor: 'secondary.main',
                                  }}
                                >
                                  <ShareIcon sx={{ fontSize: 10 }} />
                                </Avatar>
                              )}
                            </Box>

                            {/* Folder Name */}
                            <Typography 
                              variant="subtitle2" 
                              noWrap 
                              sx={{ 
                                mb: 0.5,
                                fontWeight: 600,
                                fontSize: isMobile ? '0.875rem' : '1rem'
                              }}
                            >
                              {folder.name}
                            </Typography>

                            {/* File Count */}
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ display: 'block', mb: 1, fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                            >
                              {folder.file_count} file{folder.file_count !== 1 ? 's' : ''}
                            </Typography>
                            
                            {/* Sharing Indicator */}
                            <Box sx={{ mb: 1, minHeight: 20 }}>
                              {renderSharingIndicator(folder)}
                            </Box>
                            
                            {/* Checklist Progress */}
                            {folder.checklist_stats && folder.checklist_stats.total_items > 0 && (
                              <Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={folder.checklist_stats.completion_percentage || 0} 
                                  sx={{ 
                                    height: isMobile ? 4 : 6, 
                                    borderRadius: 3,
                                    mb: 0.5
                                  }}
                                  color={folder.checklist_stats.completion_percentage === 100 ? "success" : "primary"}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}
                                  >
                                    {folder.checklist_stats.completed_items}/{folder.checklist_stats.total_items}
                                  </Typography>
                                  {folder.checklist_stats.completion_percentage === 100 && (
                                    <CheckSquareIcon sx={{ fontSize: 12, color: 'success.main' }} />
                                  )}
                                </Box>
                              </Box>
                            )}
                            
                            {/* Quick Actions */}
                            {!selectionMode && (
                              <Box sx={{ 
                                position: 'absolute', 
                                top: isMobile ? 4 : 8, 
                                right: isMobile ? 4 : 8,
                                display: 'flex',
                                gap: 0.5
                              }}>
                                <Tooltip title="Open">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/organizers/${folder.id}`);
                                    }}
                                    sx={{ 
                                      bgcolor: 'rgba(255,255,255,0.9)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                      width: isMobile ? 24 : 28,
                                      height: isMobile ? 24 : 28,
                                    }}
                                  >
                                    <LaunchIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="More">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, { type: 'organizer', data: folder })}
                                    sx={{ 
                                      bgcolor: 'rgba(255,255,255,0.9)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                      width: isMobile ? 24 : 28,
                                      height: isMobile ? 24 : 28,
                                    }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {(filteredFolders.length > 0 && filteredFiles.length > 0) && (
                <Divider sx={{ my: 3 }} />
              )}

              {/* Unorganized Files Section */}
              {filteredFiles.length > 0 && (
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 2,
                    px: isMobile ? 0.5 : 0 
                  }}>
                    <InsertDriveFile color="primary" fontSize="small" />
                    <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600 }}>
                      Unorganized Files ({filteredFiles.length})
                    </Typography>
                    {selectionMode && getSelectedFiles().length > 0 && (
                      <Chip 
                        size="small" 
                        label={`${getSelectedFiles().length} selected`} 
                        color="primary" 
                      />
                    )}
                  </Box>
                  
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                    {filteredFiles.map(file => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
                        <Card
                          onClick={() => handleItemClick(file, 'file')}
                          sx={{
                            cursor: 'pointer',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.2s',
                            border: isItemSelected(file.id, 'file') ? 2 : 1,
                            borderColor: isItemSelected(file.id, 'file') ? 'primary.main' : 'divider',
                            bgcolor: isItemSelected(file.id, 'file') ? 'primary.lighter' : 'background.paper',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                        >
                          {/* File Preview/Icon */}
                          <Box sx={{ 
                            position: 'relative', 
                            height: isMobile ? 100 : 120, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'action.hover',
                            borderBottom: 1,
                            borderColor: 'divider'
                          }}>
                            {selectionMode && (
                              <Checkbox
                                checked={isItemSelected(file.id, 'file')}
                                onChange={() => toggleItemSelection(file.id, 'file')}
                                sx={{ 
                                  position: 'absolute', 
                                  top: 4, 
                                  right: 4, 
                                  zIndex: 1,
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                              />
                            )}
                            {isImageFile(file.filename) ? (
                              <img
                                src={file.file_url}
                                alt={file.filename}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              renderFileIcon(file, isMobile ? 'large' : 'large')
                            )}
                          </Box>

                          {/* File Info */}
                          <CardContent sx={{ 
                            flexGrow: 1,
                            p: isMobile ? 1 : 1.5,
                            '&:last-child': { pb: isMobile ? 1 : 1.5 }
                          }}>
                            <Typography 
                              variant="caption" 
                              noWrap 
                              sx={{ 
                                display: 'block',
                                fontWeight: 500,
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                mb: 0.5
                              }}
                            >
                              {file.name || file.filename}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}
                            >
                              {new Date(file.created_at || file.creation_date).toLocaleDateString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          ) : (
            /* List View */
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
                  <TableRow>
                    {selectionMode && <TableCell padding="checkbox"></TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Sharing</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Modified</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFolders.map(folder => (
                    <TableRow 
                      key={`folder_${folder.id}`}
                      hover
                      onClick={() => handleItemClick(folder, 'folder')}
                      sx={{ 
                        cursor: selectionMode ? 'default' : 'pointer',
                        opacity: selectionMode ? 0.7 : 1
                      }}
                    >
                      {selectionMode && <TableCell padding="checkbox"></TableCell>}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Folder color={folder.access_type === 'shared' ? 'secondary' : 'primary'} />
                          <Box>
                            <Typography fontWeight={500} sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                              {folder.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {folder.file_count} files
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell>
                            <Chip label="Folder" size="small" variant="outlined" color="primary" />
                          </TableCell>
                          <TableCell>{renderSharingIndicator(folder)}</TableCell>
                          <TableCell>
                            {new Date(folder.updated_at).toLocaleDateString()}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        {!selectionMode && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Open">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/organizer/${folder.id}`);
                                }}
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, { type: 'organizer', data: folder })}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredFiles.map(file => (
                    <TableRow 
                      key={`file_${file.id}`}
                      hover
                      onClick={() => handleItemClick(file, 'file')}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: isItemSelected(file.id, 'file') ? 'action.selected' : 'transparent'
                      }}
                    >
                      {selectionMode && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected(file.id, 'file')}
                            onChange={() => toggleItemSelection(file.id, 'file')}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {renderFileIcon(file, 'small')}
                          <Typography sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                            {file.name || file.filename}
                          </Typography>
                        </Box>
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell>
                            <Chip 
                              label={file.type === 'contract' ? 'Contract' : 'File'} 
                              size="small" 
                              variant="outlined" 
                              color={file.type === 'contract' ? 'secondary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              Unorganized
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(file.created_at || file.creation_date).toLocaleDateString()}
                          </TableCell>
                        </>
                      )}
                      <TableCell></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Empty State */}
          {filteredFolders.length === 0 && filteredFiles.length === 0 && (
            <Paper sx={{ textAlign: 'center', py: 8, px: 2 }}>
              <FolderOpenIcon sx={{ fontSize: isMobile ? 64 : 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant={isMobile ? 'h6' : 'h5'} color="text.secondary" gutterBottom>
                {searchQuery ? 'No results found' : 
                 viewFilter === 'shared' ? 'No shared folders' :
                 viewFilter === 'owned' ? 'No folders found' :
                 'No files or folders'}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {searchQuery 
                  ? 'Try adjusting your search terms' 
                  : viewFilter === 'shared'
                    ? 'No folders have been shared with you yet'
                  : 'Upload files or create folders to get started'
                }
              </Typography>
              {!searchQuery && !isMobile && (
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    onClick={() => setCreateOrganizerDialogOpen(true)}
                    startIcon={<CreateNewFolderIcon />}
                  >
                    Create Folder
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={openUploadDialog}
                    startIcon={<UploadFile />}
                  >
                    Upload Files
                  </Button>
                </Stack>
              )}
            </Paper>
          )}
        </Box>
      </Container>

      {/* Mobile Speed Dial - Positioned above AppShell nav */}
      {isMobile && (
        <SpeedDial
          ariaLabel="Quick Actions"
          sx={{ 
            position: 'fixed', 
            bottom: `calc(${CONTENT_BOTTOM_PADDING}px + env(safe-area-inset-bottom, 0px))`,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
          icon={<SpeedDialIcon />}
          direction="up"
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.action}
            />
          ))}
        </SpeedDial>
      )}

      {/* Context Menu */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuTarget?.type === 'organizer' && [
          <MenuItem key="open" onClick={() => {
            navigate(`/organizer/${menuTarget.data.id}`);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>,
          <Divider key="divider1" />,
          !menuTarget.data.checklist_stats || menuTarget.data.checklist_stats.total_items === 0 ? (
            <MenuItem key="createChecklist" onClick={() => handleCreateChecklist(menuTarget.data)}>
              <ListItemIcon>
                <AddTaskIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Create Checklist</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem key="manageChecklist" onClick={() => {
              navigate(`/organizer/${menuTarget.data.id}`);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <CheckSquareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage Checklist</ListItemText>
            </MenuItem>
          ),
          <MenuItem key="uploadChecklist" onClick={() => handleUploadWithChecklist(menuTarget.data)}>
            <ListItemIcon>
              <CloudUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload with Checklist</ListItemText>
          </MenuItem>,
          <Divider key="divider2" />,
          menuTarget.data.access_type === 'owner' && [
            <MenuItem key="share" onClick={() => handleShareOrganizer(menuTarget.data)}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share</ListItemText>
            </MenuItem>,
            menuTarget.data.is_shared && (
              <MenuItem key="viewShares" onClick={() => handleViewShares(menuTarget.data)}>
                <ListItemIcon>
                  <GroupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Shares</ListItemText>
              </MenuItem>
            )
          ],
          <Divider key="divider3" />,
          <MenuItem key="edit" onClick={() => handleEditOrganizer(menuTarget.data)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>,
          menuTarget.data.access_type === 'owner' && (
            <MenuItem 
              key="delete" 
              onClick={() => handleDeleteOrganizer(menuTarget.data.id)} 
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          )
        ]}
      </Menu>

      {/* Dialogs - All with fullScreen on mobile */}
      
      {/* Create Folder Dialog */}
      <Dialog 
        open={createOrganizerDialogOpen} 
        onClose={() => setCreateOrganizerDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth 
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Create New Folder</Typography>
            {isMobile && (
              <IconButton onClick={() => setCreateOrganizerDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newOrganizerForm.name}
            onChange={(e) => setNewOrganizerForm({...newOrganizerForm, name: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newOrganizerForm.description}
            onChange={(e) => setNewOrganizerForm({...newOrganizerForm, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            options={organizerTypes}
            getOptionLabel={(option) => option.label}
            value={organizerTypes.find(type => type.value === newOrganizerForm.organizer_type) || null}
            onChange={(event, newValue) => {
              setNewOrganizerForm({...newOrganizerForm, organizer_type: newValue?.value || 'general'});
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Folder Type"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {!isMobile && (
            <Button onClick={() => setCreateOrganizerDialogOpen(false)}>Cancel</Button>
          )}
          <Button
            onClick={handleCreateOrganizer}
            variant="contained"
            disabled={!newOrganizerForm.name || createOrganizerMutation.isLoading}
            startIcon={<SaveIcon />}
            fullWidth={isMobile}
          >
            {createOrganizerMutation.isLoading ? 'Creating...' : 'Create Folder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog 
        open={editOrganizerDialogOpen} 
        onClose={() => setEditOrganizerDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth 
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Edit Folder</Typography>
            {isMobile && (
              <IconButton onClick={() => setEditOrganizerDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingOrganizer && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Folder Name"
                fullWidth
                variant="outlined"
                value={editingOrganizer.name}
                onChange={(e) => setEditingOrganizer({...editingOrganizer, name: e.target.value})}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Description"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={editingOrganizer.description || ''}
                onChange={(e) => setEditingOrganizer({...editingOrganizer, description: e.target.value})}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                options={organizerTypes}
                getOptionLabel={(option) => option.label}
                value={organizerTypes.find(type => type.value === editingOrganizer.organizer_type) || null}
                onChange={(event, newValue) => {
                  setEditingOrganizer({...editingOrganizer, organizer_type: newValue?.value || 'general'});
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Folder Type"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {!isMobile && (
            <Button onClick={() => setEditOrganizerDialogOpen(false)}>Cancel</Button>
          )}
          <Button
            onClick={handleUpdateOrganizer}
            variant="contained"
            disabled={!editingOrganizer?.name || updateOrganizerMutation.isLoading}
            startIcon={<SaveIcon />}
            fullWidth={isMobile}
          >
            {updateOrganizerMutation.isLoading ? 'Updating...' : 'Update Folder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Files Dialog */}
      <Dialog 
        open={addFilesToOrganizerDialogOpen} 
        onClose={() => {
          setAddFilesToOrganizerDialogOpen(false);
          setSelectedOrganizerForAdd(null);
        }} 
        maxWidth="sm" 
        fullWidth 
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Move Files to Folder</Typography>
            {isMobile && (
              <IconButton onClick={() => {
                setAddFilesToOrganizerDialogOpen(false);
                setSelectedOrganizerForAdd(null);
              }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Moving {getSelectedFiles().length} file{getSelectedFiles().length !== 1 ? 's' : ''} to a folder
          </Alert>
          
          {getSelectedFiles().length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Selected files:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {getSelectedFiles().map(fileId => {
                  const file = files.find(f => f.id === fileId);
                  return file ? (
                    <Chip 
                      key={fileId} 
                      label={file.filename || file.name} 
                      size="small" 
                    />
                  ) : null;
                })}
              </Box>
            </Box>
          )}

          <Autocomplete
            options={organizers || []}
            getOptionLabel={(option) => `${option.name}${option.organizer_type ? ` (${option.organizer_type})` : ''}`}
            value={selectedOrganizerForAdd}
            onChange={(event, newValue) => setSelectedOrganizerForAdd(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Destination Folder"
                placeholder="Choose a folder..."
                margin="normal"
                fullWidth
                helperText="Select which folder to move the files to"
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Folder color="primary" />
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.organizer_type} • {option.file_count} files
                    </Typography>
                  </Box>
                </Box>
              </li>
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {!isMobile && (
            <Button onClick={() => {
              setAddFilesToOrganizerDialogOpen(false);
              setSelectedOrganizerForAdd(null);
            }}>
              Cancel
            </Button>
          )}
          <Button
            onClick={handleAddFilesToOrganizer}
            variant="contained"
            disabled={!selectedOrganizerForAdd || getSelectedFiles().length === 0 || addFilesToOrganizerMutation.isLoading}
            fullWidth={isMobile}
          >
            {addFilesToOrganizerMutation.isLoading ? 'Moving...' : `Move ${getSelectedFiles().length} File${getSelectedFiles().length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Checklist Dialog */}
      <Dialog 
        open={createChecklistDialogOpen} 
        onClose={() => {
          setCreateChecklistDialogOpen(false);
          setNewChecklistItem({
            title: '',
            description: '',
            required: true,
            expected_file_types: [],
            notes: ''
          });
          setBulkChecklistText('');
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddTaskIcon />
              <Typography variant="h6">
                Create Checklist for {selectedOrganizerForChecklist?.name}
              </Typography>
            </Box>
            {isMobile && (
              <IconButton onClick={() => {
                setCreateChecklistDialogOpen(false);
                setNewChecklistItem({
                  title: '',
                  description: '',
                  required: true,
                  expected_file_types: [],
                  notes: ''
                });
                setBulkChecklistText('');
              }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={checklistView} onChange={(e, newValue) => setChecklistView(newValue)} sx={{ mb: 2 }}>
            <Tab label="Single Item" value="single" />
            <Tab label="Bulk Create" value="bulk" />
          </Tabs>

          {checklistView === 'single' && (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Item Title"
                fullWidth
                variant="outlined"
                value={newChecklistItem.title}
                onChange={(e) => setNewChecklistItem({...newChecklistItem, title: e.target.value})}
                sx={{ mb: 2 }}
                placeholder="e.g., Financial Statements, Tax Returns, etc."
              />
              <TextField
                margin="dense"
                label="Description"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={newChecklistItem.description}
                onChange={(e) => setNewChecklistItem({...newChecklistItem, description: e.target.value})}
                sx={{ mb: 2 }}
                placeholder="Detailed description of what's expected..."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newChecklistItem.required}
                    onChange={(e) => setNewChecklistItem({...newChecklistItem, required: e.target.checked})}
                  />
                }
                label="Required Item"
                sx={{ mb: 2 }}
              />
              <Autocomplete
                multiple
                freeSolo
                options={['pdf', 'xlsx', 'docx', 'jpg', 'png', 'txt']}
                value={newChecklistItem.expected_file_types}
                onChange={(event, newValue) => setNewChecklistItem({...newChecklistItem, expected_file_types: newValue})}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Expected File Types"
                    placeholder="Add file types..."
                    variant="outlined"
                    helperText="Leave empty to accept any file type"
                  />
                )}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Notes"
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={newChecklistItem.notes}
                onChange={(e) => setNewChecklistItem({...newChecklistItem, notes: e.target.value})}
                placeholder="Additional notes or instructions..."
              />
            </Box>
          )}

          {checklistView === 'bulk' && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Enter one checklist item per line. Each line will become a separate required item.
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 12 : 10}
                variant="outlined"
                value={bulkChecklistText}
                onChange={(e) => setBulkChecklistText(e.target.value)}
                placeholder={`Financial Statements (3 years)
Tax Returns
Bank Statements
Legal Documents
Insurance Policies
Customer Contracts
Employee Records`}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary">
                {bulkChecklistText.split('\n').filter(line => line.trim()).length} items will be created
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {!isMobile && (
            <Button onClick={() => {
              setCreateChecklistDialogOpen(false);
              setNewChecklistItem({
                title: '',
                description: '',
                required: true,
                expected_file_types: [],
                notes: ''
              });
              setBulkChecklistText('');
            }}>
              Cancel
            </Button>
          )}
          {checklistView === 'single' && (
            <Button
              onClick={handleCreateChecklistItem}
              variant="contained"
              disabled={!newChecklistItem.title || createChecklistItemMutation.isLoading}
              startIcon={<AddTaskIcon />}
              fullWidth={isMobile}
            >
              {createChecklistItemMutation.isLoading ? 'Creating...' : 'Create Item'}
            </Button>
          )}
          {checklistView === 'bulk' && (
            <Button
              onClick={handleBulkCreate}
              variant="contained"
              disabled={!bulkChecklistText.trim() || createBulkChecklistItemsMutation.isLoading}
              startIcon={<PlaylistAddCheckIcon />}
              fullWidth={isMobile}
            >
              {createBulkChecklistItemsMutation.isLoading ? 'Creating...' : 'Create Items'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Upload with Checklist Dialog */}
      <Dialog 
        open={uploadWithChecklistDialogOpen} 
        onClose={() => {
          setUploadWithChecklistDialogOpen(false);
          setSelectedOrganizerForChecklist(null);
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudUploadIcon />
              <Typography variant="h6">
                Upload Files to {selectedOrganizerForChecklist?.name}
              </Typography>
            </Box>
            {isMobile && (
              <IconButton onClick={() => {
                setUploadWithChecklistDialogOpen(false);
                setSelectedOrganizerForChecklist(null);
              }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Upload files and automatically assign them to checklist items. 
            Files will be matched based on name similarity and expected file types.
          </Alert>
          
          {selectedOrganizerForChecklist && selectedOrganizerForChecklist.checklist_stats && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Checklist Progress
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={selectedOrganizerForChecklist.checklist_stats.completion_percentage || 0} 
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
                color={selectedOrganizerForChecklist.checklist_stats.completion_percentage === 100 ? "success" : "primary"}
              />
              <Typography variant="caption" color="text.secondary">
                {selectedOrganizerForChecklist.checklist_stats.completed_items} of {selectedOrganizerForChecklist.checklist_stats.total_items} items completed
              </Typography>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<UploadFile />}
              onClick={() => {
                openUploadDialog();
                setUploadWithChecklistDialogOpen(false);
              }}
              fullWidth={isMobile}
            >
              Choose Files to Upload
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setUploadWithChecklistDialogOpen(false);
              setSelectedOrganizerForChecklist(null);
            }}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Organizer Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => {
          setShareDialogOpen(false);
          setShareForm({ emails: [], permission_level: 'read', message: '' });
          setShareEmailInput('');
        }} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShareIcon />
              <Typography variant="h6">
                Share "{selectedOrganizerForShare?.name}"
              </Typography>
            </Box>
            {isMobile && (
              <IconButton onClick={() => {
                setShareDialogOpen(false);
                setShareForm({ emails: [], permission_level: 'read', message: '' });
                setShareEmailInput('');
              }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this folder with other users by entering their email addresses.
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter email address"
                value={shareEmailInput}
                onChange={(e) => setShareEmailInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddEmail();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddEmail}
                startIcon={<PersonAddIcon />}
                disabled={!shareEmailInput.trim()}
              >
                Add
              </Button>
            </Box>

            {shareForm.emails.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sharing with ({shareForm.emails.length}):
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {shareForm.emails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveEmail(email)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Permission Level</InputLabel>
              <Select
                value={shareForm.permission_level}
                onChange={(e) => setShareForm(prev => ({ ...prev, permission_level: e.target.value }))}
                label="Permission Level"
              >
                <MenuItem value="read">
                  <Box>
                    <Typography variant="body2">Read Only</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Can view files and folders
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="write">
                  <Box>
                    <Typography variant="body2">Can Edit</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Can add, edit, and organize files
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box>
                    <Typography variant="body2">Full Access</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Can manage sharing and all settings
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message (optional)"
              placeholder="Add a personal message..."
              value={shareForm.message}
              onChange={(e) => setShareForm(prev => ({ ...prev, message: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {!isMobile && (
            <Button onClick={() => {
              setShareDialogOpen(false);
              setShareForm({ emails: [], permission_level: 'read', message: '' });
              setShareEmailInput('');
            }}>
              Cancel
            </Button>
          )}
          <Button
            onClick={handleShareSubmit}
            variant="contained"
            disabled={shareForm.emails.length === 0 || shareOrganizerMutation.isLoading}
            startIcon={<ShareIcon />}
            fullWidth={isMobile}
          >
            {shareOrganizerMutation.isLoading ? 'Sharing...' : `Share with ${shareForm.emails.length} user${shareForm.emails.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Shares Dialog */}
      <Dialog 
        open={viewSharesDialogOpen} 
        onClose={() => {
          setViewSharesDialogOpen(false);
          setSelectedOrganizerForShare(null);
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon />
              <Typography variant="h6">
                Sharing "{selectedOrganizerForShare?.name}"
              </Typography>
            </Box>
            {isMobile && (
              <IconButton onClick={() => {
                setViewSharesDialogOpen(false);
                setSelectedOrganizerForShare(null);
              }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrganizerForShare?.shares?.length > 0 ? (
            <List>
              {selectedOrganizerForShare.shares.map((share, index) => (
                <React.Fragment key={share.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {share.shared_with_email}
                          </Typography>
                          <Chip 
                            label={share.permission_level} 
                            size="small" 
                            color={share.permission_level === 'admin' ? 'error' : 
                                   share.permission_level === 'write' ? 'warning' : 'default'}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption">
                            Shared {new Date(share.created_at).toLocaleDateString()}
                          </Typography>
                          {share.share_message && (
                            <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
                              "{share.share_message}"
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveShare(share.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < selectedOrganizerForShare.shares.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <GroupIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                This folder is not shared with anyone yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {!isMobile && (
            <Button onClick={() => {
              setViewSharesDialogOpen(false);
              setSelectedOrganizerForShare(null);
            }}>
              Close
            </Button>
          )}
          <Button
            onClick={() => {
              setViewSharesDialogOpen(false);
              handleShareOrganizer(selectedOrganizerForShare);
            }}
            variant="contained"
            startIcon={<PersonAddIcon />}
            fullWidth={isMobile}
          >
            Add People
          </Button>
        </DialogActions>
      </Dialog>

      {/* Existing Dialogs */}
      <UploadFileDialog 
        open={uploadDialogOpen} 
        onClose={closeUploadDialog}
        organizerContext={selectedOrganizerForChecklist}
      />
      <CreateAssetDialog open={createAssetDialogOpen} onClose={closeCreateAssetDialog} />
      <CreateContractDialog open={createContractDialogOpen} onClose={closeCreateContractDialog} onSuccess={handleSuccess} />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssetOrganizerSeg;